import Raven from 'raven-js';
// @ts-expect-error build-time virtual module
import { bender } from 'legacy-hubspot-bender-context';
import { fetchGraniteRemote, loadRemoteDlbs, loadRemoteModule, registerRemoteModules } from '../internal/module-federation/runtime';
import { findMostSpecificRoute } from 'quartz-routing/internal/routeMatchingUtils';
import { bindLinkRewriter } from './linkRewriter';
import { getAuthResult } from 'quartz-auth/service';
import { getI18nProvider } from 'quartz-i18n/service';
import { setCopilotLayoutType } from 'quartz/framework/copilot/service';
import { updateGridClasses } from '../internal/grid';
import { reconfigure } from 'raven-hubspot/configure';
import PortalIdParser from 'PortalIdParser';
import { HistoryManager } from './HistoryManager';
import { RemoteCssManager } from './RemoteCssManager';
import { setup } from '../internal/module-federation/init';
function metricsSafePathname() {
  const pathname = window.location.pathname;
  const portalId = PortalIdParser.get();
  if (portalId) {
    const endOfStringMatcher = new RegExp(`/${portalId}$`);
    return pathname.replace(`/${portalId}/`, `/:portalId/`).replace(endOfStringMatcher, '/:portalId');
  }
  return pathname;
}
export class GraniteRuntime {
  constructor(cssManager, historyManager) {
    this._activeApp = null;
    this._localAppName = null;
    this._transitionContext = null;
    this._appCache = new Map();
    this._pendingTransition = null;
    this._localRouteOverrides = {};
    this._cssManager = cssManager !== null && cssManager !== void 0 ? cssManager : new RemoteCssManager();
    this._historyManager = historyManager !== null && historyManager !== void 0 ? historyManager : new HistoryManager();
  }
  get activeApp() {
    var _this$_activeApp$appN, _this$_activeApp;
    return (_this$_activeApp$appN = (_this$_activeApp = this._activeApp) === null || _this$_activeApp === void 0 ? void 0 : _this$_activeApp.appName) !== null && _this$_activeApp$appN !== void 0 ? _this$_activeApp$appN : null;
  }
  get transitionContext() {
    return this._transitionContext;
  }
  get cssManager() {
    return this._cssManager;
  }
  get historyManager() {
    return this._historyManager;
  }
  initialize(remoteModules, localRouteOverrides) {
    setup();
    registerRemoteModules(remoteModules);
    this._localRouteOverrides = localRouteOverrides;
  }
  registerLocalApp(app) {
    this._localAppName = app.name;
    this._appCache.set(app.name, app);
  }
  getAppForPath(pathname) {
    var _this$_localRouteOver;
    const matchedKey = findMostSpecificRoute(Object.keys(this._localRouteOverrides), pathname);
    if (!matchedKey) return this._localAppName;
    return (_this$_localRouteOver = this._localRouteOverrides[matchedKey].remoteModule) !== null && _this$_localRouteOver !== void 0 ? _this$_localRouteOver : this._localAppName;
  }
  startListening(history, getContainer, callbacks) {
    const unbindLinks = bindLinkRewriter(history, this._localRouteOverrides);
    const unbindPopstate = this._historyManager.setupPopstateHandling(this._localRouteOverrides);
    const initialApp = this.getAppForPath(history.location.pathname);
    if (initialApp) {
      const container = getContainer();
      if (container) {
        this.transition(initialApp, container).catch(e => {
          this._reportError(e, initialApp);
          callbacks.onError(e);
        });
      }
    }
    const unblockNav = history.block(() => {
      var _this$_activeApp2;
      if (!((_this$_activeApp2 = this._activeApp) !== null && _this$_activeApp2 !== void 0 && _this$_activeApp2.onBeforeAppUnload)) return;
      let defaultPrevented = false;
      this._activeApp.onBeforeAppUnload({
        get defaultPrevented() {
          return defaultPrevented;
        },
        preventDefault() {
          defaultPrevented = true;
        }
      });
      if (defaultPrevented) return false;
    });
    const unlistenHistory = history.listen(location => {
      const appName = this.getAppForPath(location.pathname);
      if (appName) {
        var _this$_activeApp3;
        if (appName !== ((_this$_activeApp3 = this._activeApp) === null || _this$_activeApp3 === void 0 ? void 0 : _this$_activeApp3.appName)) {
          const container = getContainer();
          if (container) {
            callbacks.onError(null);
            this.transition(appName, container).catch(e => {
              this._reportError(e, appName);
              callbacks.onError(e);
            });
          }
        }
        callbacks.onAppActive(appName);
      } else {
        // No app for this path: deactivate whatever was active and signal
        // that nothing is mounted.
        if (this._activeApp) {
          this.deactivateActiveApp();
        }
        callbacks.onError(null);
        callbacks.onAppActive(null);
      }
    });
    return () => {
      unlistenHistory();
      unblockNav();
      unbindLinks();
      unbindPopstate();
      this.deactivateActiveApp();
    };
  }
  captureTransitionContext() {
    if (!this._activeApp) {
      return undefined;
    }
    return {
      timestamp: performance.now(),
      fromPathname: metricsSafePathname(),
      fromRoute: metricsSafePathname(),
      fromRemoteApp: this._activeApp.appName
    };
  }
  async prepare(appName) {
    if (this._appCache.has(appName)) {
      return this._appCache.get(appName);
    }
    reconfigure({
      project: appName
    });
    const dlbsPromise = loadRemoteDlbs(appName);
    const remoteContainerPromise = fetchGraniteRemote(appName);
    const langPromise = loadRemoteModule(appName, 'lang').then(mod => mod.LANG);
    const [, remoteContainer, translations] = await Promise.all([dlbsPromise, remoteContainerPromise, langPromise]);
    const remoteModule = remoteContainer.loadEntry();
    if (!remoteModule) {
      throw new Error(`Failed to load granite remote app module "${appName}". Module undefined.`);
    }
    const historyManager = this._historyManager;
    const cssManager = this._cssManager;
    const app = {
      name: appName,
      copilotLayoutType: remoteModule.copilotLayoutType,
      async render({
        container,
        softNavigationContext
      }) {
        await cssManager.switchTo(appName);
        const reactRoot = await remoteModule.render({
          softNavigationContext,
          container,
          authResult: getAuthResult(),
          translations,
          localeInfo: getI18nProvider().localeInfo,
          attachHistory: historyManager.createAttachHistory(appName)
        });
        return {
          appName,
          onBeforeAppUnload: reactRoot === null || reactRoot === void 0 ? void 0 : reactRoot.onBeforeAppUnload,
          unmount: () => reactRoot === null || reactRoot === void 0 ? void 0 : reactRoot.unmount()
        };
      }
    };
    this._appCache.set(appName, app);
    return app;
  }
  async activate(app, container, softNavigationContext) {
    const {
      copilotLayoutType
    } = app;
    if (copilotLayoutType !== undefined) {
      setCopilotLayoutType(copilotLayoutType);
      updateGridClasses(copilotLayoutType);
    }
    const handle = await app.render({
      container,
      softNavigationContext
    });
    this._activeApp = handle;
    return handle;
  }
  deactivateActiveApp() {
    if (!this._activeApp) return;
    this._transitionContext = {
      fromApp: this._activeApp.appName,
      fromPathname: metricsSafePathname(),
      timestamp: performance.now()
    };

    // Disconnect history before unmount so cleanup effects in the old remote
    // cannot call replaceState and corrupt window.location.pathname before the
    // new remote reads it in initializeNestedHistory.
    this._historyManager.disconnect();
    this._activeApp.unmount();
    this._activeApp = null;
  }
  cleanup() {
    this._appCache.clear();
    this._localAppName = null;
  }
  async transition(appName, container) {
    var _this$_activeApp4;
    if (((_this$_activeApp4 = this._activeApp) === null || _this$_activeApp4 === void 0 ? void 0 : _this$_activeApp4.appName) === appName) return;
    if (this._pendingTransition) {
      this._pendingTransition.cancelled = true;
    }
    const pending = {
      appName,
      cancelled: false
    };
    this._pendingTransition = pending;

    // Capture from-state before any async work
    const softNavigationContext = this.captureTransitionContext();
    this.deactivateActiveApp();
    const app = await this.prepare(appName);
    if (pending.cancelled) return;
    await this.activate(app, container, softNavigationContext);
    this._pendingTransition = null;
  }
  preload(appName) {
    this.prepare(appName).catch(() => {});
  }
  resetForTesting() {
    this.deactivateActiveApp();
    this.cleanup();
    this._transitionContext = null;
    this._localRouteOverrides = {};
    this._cssManager.resetForTesting();
  }
  _reportError(error, appName) {
    Raven.captureException(error, {
      tags: {
        project: appName
      },
      extra: {
        hostProject: bender.currentProject,
        hostRelease: bender.currentProjectVersion
      }
    });
  }
}
let runtimeInstance = null;
export function getGraniteRuntime() {
  if (!runtimeInstance) {
    runtimeInstance = new GraniteRuntime();
  }
  return runtimeInstance;
}
export function resetGraniteRuntimeForTesting() {
  if (runtimeInstance) {
    runtimeInstance.resetForTesting();
  }
  runtimeInstance = null;
}